/**
 * Ticket 114.3 — Demo list page.
 *
 * Table view of demos for the current tenant. Requires demo.read permission
 * (enforced via ProtectedRoute in App.tsx).
 *
 * Columns: business name (link to editor), industry (key), country flag,
 * status (badge), opportunity link (if any), owner (createdBy), updated_at.
 * Filters: status, industry key, opportunity text search, owner UUID.
 * Empty state shown when no demos exist.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDemos, type DemoFilters } from '@/pages/demos/hooks/useDemos';
import type { DemoStatus } from '@/api/demos';

const COUNTRY_FLAGS: Record<string, string> = {
  PR: '🇵🇷',
  CO: '🇨🇴',
};

const STATUS_BADGE_CLASSES: Record<DemoStatus, string> = {
  DRAFT: 'bg-zinc-800/60 text-zinc-300',
  AWAITING_REFINEMENT: 'bg-amber-900/40 text-amber-200',
  READY_TO_SEND: 'bg-blue-900/40 text-blue-200',
  PUBLISHED: 'bg-emerald-900/40 text-emerald-200',
  ARCHIVED: 'bg-muted text-muted-foreground',
};

const ALL_STATUSES: DemoStatus[] = [
  'DRAFT',
  'AWAITING_REFINEMENT',
  'READY_TO_SEND',
  'PUBLISHED',
  'ARCHIVED',
];

export function DemoListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<DemoFilters>({});
  const { demos, loading } = useDemos(filters);

  function setFilter<K extends keyof DemoFilters>(key: K, value: DemoFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  }

  const statusLabel = (s: DemoStatus) => t(`demos.status.${camelStatus(s)}`);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('demos.list.title')}</h1>
        <Button asChild>
          <Link to="/demos/new">{t('demos.list.new')}</Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Status filter */}
        <select
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
          value={filters.status ?? ''}
          onChange={(e) => setFilter('status', e.target.value || undefined)}
          aria-label={t('demos.list.filter.status')}
        >
          <option value="">{t('demos.list.filter.status')}</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>

        {/* Industry key filter */}
        <Input
          className="w-44"
          placeholder={t('demos.list.filter.industry')}
          value={filters.industryKey ?? ''}
          onChange={(e) => setFilter('industryKey', e.target.value)}
        />

        {/* Opportunity / business name text search */}
        <Input
          className="w-52"
          placeholder={t('demos.list.filter.opportunity')}
          value={filters.opportunity ?? ''}
          onChange={(e) => setFilter('opportunity', e.target.value)}
        />
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : demos.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-20 text-muted-foreground">
          <p className="text-base">{t('demos.list.empty')}</p>
          <Button variant="secondary" asChild>
            <Link to="/demos/new">
              <Plus className="h-4 w-4 mr-1" />
              {t('demos.list.new')}
            </Link>
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left">{t('demos.list.column.businessName')}</th>
                <th className="px-4 py-2 text-left">{t('demos.list.column.industry')}</th>
                <th className="px-4 py-2 text-left">{t('demos.list.column.country')}</th>
                <th className="px-4 py-2 text-left">{t('demos.list.column.status')}</th>
                <th className="px-4 py-2 text-left">{t('demos.list.column.opportunity')}</th>
                <th className="px-4 py-2 text-left">{t('demos.list.column.updatedAt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {demos.map((demo) => (
                <tr key={demo.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-2.5 font-medium">
                    <Link
                      to={`/demos/${demo.id}`}
                      className="text-[color:var(--color-lop-teal)] hover:underline"
                    >
                      {demo.businessName}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{demo.industryKey}</td>
                  <td className="px-4 py-2.5">
                    <span title={demo.country} aria-label={demo.country}>
                      {COUNTRY_FLAGS[demo.country.toUpperCase()] ?? demo.country}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[demo.status]}`}
                    >
                      {statusLabel(demo.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {demo.opportunityId ? (
                      <Link
                        to={`/opportunities/${demo.opportunityId}`}
                        className="text-[color:var(--color-lop-teal)] hover:underline text-xs"
                      >
                        {t('demos.list.column.opportunity')}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">
                    {new Date(demo.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function camelStatus(s: DemoStatus): string {
  const map: Record<DemoStatus, string> = {
    DRAFT: 'draft',
    AWAITING_REFINEMENT: 'awaitingRefinement',
    READY_TO_SEND: 'readyToSend',
    PUBLISHED: 'published',
    ARCHIVED: 'archived',
  };
  return map[s] ?? s;
}
